import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CloudArrowUp, WarningCircle, WifiSlash, CheckCircle, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { visitsAPI } from '../services/api';
import {
  isOnline,
  subscribe as subscribeQueue,
  getQueue,
  syncQueue,
  removeAction,
} from '../services/offlineQueue';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { format } from 'date-fns';

const CONFLICT_META = {
  reassigned: { title: 'Visit Reassigned', color: 'bg-amber-50 border-amber-300 text-amber-900' },
  visit_deleted: { title: 'Visit Deleted', color: 'bg-red-50 border-red-300 text-red-900' },
  already_completed: { title: 'Already Completed', color: 'bg-slate-50 border-slate-300 text-slate-900' },
  already_checked_in: { title: 'Already Checked In', color: 'bg-blue-50 border-blue-300 text-blue-900' },
};

/**
 * Hook: returns { online, pending } and triggers auto-sync whenever connectivity returns.
 * Drop into the Layout once.
 */
export const useOfflineSync = () => {
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(0);
  const [conflicts, setConflicts] = useState([]);
  const syncing = useRef(false);

  const refreshCount = useCallback(async () => {
    try {
      const q = await getQueue();
      setPending(q.length);
      setConflicts(q.filter(a => a.conflict_kind));
    } catch (e) { setPending(0); setConflicts([]); }
  }, []);

  const drain = useCallback(async () => {
    if (syncing.current || !isOnline()) return;
    syncing.current = true;
    try {
      const before = (await getQueue()).length;
      if (before === 0) return;
      const res = await syncQueue(visitsAPI);
      if (res.synced > 0) {
        toast.success(`Synced ${res.synced} pending clock ${res.synced === 1 ? 'action' : 'actions'} to the server`);
      }
      if (res.failed > 0) {
        const q = await getQueue();
        const conflictCount = q.filter(a => a.conflict_kind).length;
        if (conflictCount > 0) {
          toast.error(`${conflictCount} action${conflictCount > 1 ? 's' : ''} need your attention — tap the orange bar to review`);
        } else {
          toast.error(`${res.failed} action${res.failed > 1 ? 's' : ''} could not sync — will retry`);
        }
      }
    } finally {
      syncing.current = false;
      refreshCount();
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    const onUp = () => { setOnline(true); drain(); };
    const onDown = () => setOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    const unsub = subscribeQueue(() => refreshCount());
    // periodic sync attempt every 30s if items queued
    const interval = setInterval(() => { if (isOnline()) drain(); }, 30000);
    // initial drain on mount in case we are already online with backlog
    drain();
    return () => {
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
      unsub();
      clearInterval(interval);
    };
  }, [drain, refreshCount]);

  return { online, pending, conflicts, drainNow: drain, refresh: refreshCount };
};

/** Conflict resolution dialog — surfaces queued items that returned a 409. */
export const ConflictResolutionDialog = ({ open, onClose, conflicts, onResolved }) => {
  const [busyId, setBusyId] = useState(null);

  const discard = async (id) => {
    setBusyId(id);
    try {
      await removeAction(id);
      toast.success('Cleared queued action');
      onResolved && onResolved();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Sync Conflicts Need Your Attention</DialogTitle>
          <DialogDescription>These clock actions couldn&apos;t sync because the visit changed while you were offline. Review each one and decide what to do.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto" data-testid="conflicts-dialog">
          {(conflicts || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No conflicts remaining.</p>
          ) : (conflicts || []).map(item => {
            const meta = CONFLICT_META[item.conflict_kind] || CONFLICT_META.visit_deleted;
            const detail = item.conflict_detail || {};
            const isCheckIn = item.type === 'check-in';
            return (
              <div key={item.id} className={`rounded-xl border p-3 ${meta.color}`} data-testid={`conflict-${item.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{meta.title}</p>
                    <p className="text-xs opacity-90 mt-0.5">
                      Your queued <strong>{isCheckIn ? 'clock-in' : 'clock-out'}</strong> from {format(new Date(item.queued_at), 'MMM d, h:mm a')}
                    </p>
                    <p className="text-xs opacity-90 mt-1">{detail.message || item.last_error}</p>
                    {detail.reassigned_to && (
                      <p className="text-xs opacity-90 mt-1"><strong>Now assigned to:</strong> {detail.reassigned_to}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-xl gap-1 shrink-0"
                    onClick={() => discard(item.id)}
                    disabled={busyId === item.id}
                    data-testid={`discard-conflict-${item.id}`}
                  >
                    <X weight="bold" className="w-3.5 h-3.5" />
                    Discard
                  </Button>
                </div>
                <p className="text-[11px] opacity-80 mt-2 border-t border-current/20 pt-2">
                  {item.conflict_kind === 'reassigned' && 'Contact your supervisor if you actually worked this shift so it can be logged correctly.'}
                  {item.conflict_kind === 'visit_deleted' && 'The visit was removed. Discard this queued action.'}
                  {item.conflict_kind === 'already_completed' && 'Someone (or you, on another device) already closed this visit. Safe to discard.'}
                  {item.conflict_kind === 'already_checked_in' && 'A check-in was already recorded. Discard this duplicate.'}
                </p>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** A small persistent status pill near the bottom (above the bottom nav). */
export const ConnectionStatusBar = ({ user }) => {
  const { online, pending, conflicts, drainNow, refresh } = useOfflineSync();
  const [conflictsOpen, setConflictsOpen] = useState(false);
  if (!user || user.role !== 'caregiver') return null;
  const hasConflicts = conflicts.length > 0;
  if (online && pending === 0 && !hasConflicts) return null;

  return (
    <>
    <div
      className="md:hidden fixed left-2 right-2 z-40 rounded-2xl shadow-lg border text-xs flex items-center gap-2 px-3 py-2"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 64px + 8px)' }}
      data-testid="connection-status-bar"
    >
      {hasConflicts ? (
        <button
          onClick={() => setConflictsOpen(true)}
          className="flex items-center gap-2 w-full bg-red-50 border-red-300 text-red-900 -mx-3 -my-2 px-3 py-2 rounded-2xl border touch-manipulation"
          data-testid="review-conflicts-btn"
        >
          <WarningCircle weight="fill" className="w-4 h-4 shrink-0" />
          <div className="flex-1 text-left">
            <p className="font-semibold">{conflicts.length} sync conflict{conflicts.length > 1 ? 's' : ''} need review</p>
            <p className="opacity-90 text-[11px]">Tap to see what changed &amp; resolve.</p>
          </div>
        </button>
      ) : !online ? (
        <div className="flex items-center gap-2 w-full bg-amber-50 border-amber-200 text-amber-900 -mx-3 -my-2 px-3 py-2 rounded-2xl border">
          <WifiSlash weight="duotone" className="w-4 h-4 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Offline mode</p>
            <p className="opacity-90 text-[11px]">Clock-in / out are queued and will sync automatically when signal returns{pending > 0 ? ` (${pending} pending)` : ''}.</p>
          </div>
        </div>
      ) : (
        <button
          onClick={drainNow}
          className="flex items-center gap-2 w-full bg-blue-50 border-blue-200 text-blue-900 -mx-3 -my-2 px-3 py-2 rounded-2xl border touch-manipulation"
          data-testid="sync-now-btn"
        >
          <CloudArrowUp weight="duotone" className="w-4 h-4 shrink-0" />
          <div className="flex-1 text-left">
            <p className="font-semibold">{pending} pending {pending === 1 ? 'action' : 'actions'}</p>
            <p className="opacity-90 text-[11px]">Tap to sync now.</p>
          </div>
        </button>
      )}
    </div>
    <ConflictResolutionDialog
      open={conflictsOpen}
      onClose={() => setConflictsOpen(false)}
      conflicts={conflicts}
      onResolved={refresh}
    />
    </>
  );
};

/** Floating "queued" toast inline indicator caregivers can use to confirm state */
export const PendingActionBadge = ({ count }) => {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-semibold">
      <WarningCircle weight="fill" className="w-3 h-3" />
      Pending sync
    </span>
  );
};

/** Used by the synced-success toast (not exported standalone). */
export const SyncedBadge = () => (
  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full font-semibold">
    <CheckCircle weight="fill" className="w-3 h-3" /> Synced
  </span>
);
